import { useState } from 'react';
import { X, Plus, BarChart3 } from 'lucide-react';

/**
 * CreatePollModal — minimal poll builder. 2–6 single-choice options.
 */
export default function CreatePollModal({ onClose, onCreate }) {
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState(['', '']);
  const [submitting, setSubmitting] = useState(false);

  const validOptions = options.map(o => o.trim()).filter(Boolean);
  const canSubmit = question.trim().length > 0 && validOptions.length >= 2 && !submitting;

  const update = (i, v) => {
    setOptions(prev => prev.map((o, idx) => idx === i ? v : o));
  };
  const remove = (i) => {
    setOptions(prev => prev.length <= 2 ? prev : prev.filter((_, idx) => idx !== i));
  };
  const add = () => {
    setOptions(prev => prev.length < 6 ? [...prev, ''] : prev);
  };

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await onCreate({
        question: question.trim(),
        options: validOptions.map(text => ({ text })),
      });
    } catch {/* ignore */}
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 z-[10075] bg-black/40 flex items-end justify-center animate-fade-in" onClick={onClose} data-testid="create-poll-overlay">
      <div className="w-full max-w-[480px] max-h-[88vh] bg-white rounded-t-3xl flex flex-col animate-slide-up" onClick={e => e.stopPropagation()} data-testid="create-poll-sheet">
        <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 border-b border-black/[0.04]">
          <button onClick={onClose} className="text-sm font-semibold text-[#9B9EA3]">Cancel</button>
          <div className="flex items-center gap-1.5">
            <BarChart3 size={14} className="text-[#6B8A00]" />
            <p className="text-base font-bold text-[#111111]">New poll</p>
          </div>
          <button disabled={!canSubmit} onClick={submit} data-testid="create-poll-submit" className="text-sm font-bold text-[#3F5200] disabled:opacity-40">
            {submitting ? 'Posting…' : 'Post'}
          </button>
        </div>
        <div className="flex-1 overflow-y-auto hide-scrollbar px-4 py-4 space-y-3">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-[#9B9EA3]">Question</label>
            <input
              autoFocus
              maxLength={140}
              value={question}
              onChange={e => setQuestion(e.target.value)}
              placeholder="What time works best?"
              data-testid="create-poll-question"
              className="mt-1 w-full bg-[#F1F0EA] rounded-2xl px-4 py-3 text-sm font-medium focus:outline-none placeholder-[#B0B3B8]"
            />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-[#9B9EA3]">Options</label>
            <div className="space-y-2 mt-1">
              {options.map((o, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    maxLength={80}
                    value={o}
                    onChange={e => update(i, e.target.value)}
                    placeholder={`Option ${i + 1}`}
                    data-testid={`create-poll-option-${i}`}
                    className="flex-1 bg-[#F1F0EA] rounded-2xl px-4 py-2.5 text-sm focus:outline-none placeholder-[#B0B3B8]"
                  />
                  {options.length > 2 && (
                    <button onClick={() => remove(i)} data-testid={`create-poll-remove-${i}`} className="p-2 text-[#9B9EA3] active:scale-90"><X size={16} /></button>
                  )}
                </div>
              ))}
            </div>
            {options.length < 6 && (
              <button onClick={add} data-testid="create-poll-add" className="mt-2 flex items-center gap-1.5 text-xs font-bold text-[#3F5200] active:scale-95">
                <Plus size={14} /> Add option
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
